import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import MergeCustomersModal from './MergeCustomersModal';
import VehicleSelector from '../VehicleSelector';

const API = process.env.REACT_APP_API_URL || '/api';

export default function CustomerProfile({ customerId, onClose, t, onMerged }) {
  const { user: adminUser } = useAuth();
  const canEdit = ['manager', 'admin'].includes(adminUser?.role);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('identity');

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Merge from profile
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeResults, setMergeResults] = useState([]);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [showMergeSearch, setShowMergeSearch] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState(null);
  const mergeTimer = useRef(null);

  // Vehicle management state
  const [vehicleModal, setVehicleModal] = useState(null); // null=closed, {}=add, {id,...}=edit
  const [vehicleForm, setVehicleForm] = useState({ make: '', model: '', year: new Date().getFullYear(), license_plate: '', vin: '', color: '' });
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [vehicleError, setVehicleError] = useState(null);

  const cp = t.admin?.customerProfile || {};
  const mg = t.admin?.merge || {};
  const formatDate = (date) => { if (!date) return ''; const dateOnly = String(date).substring(0, 10); return new Date(dateOnly + 'T12:00:00').toLocaleDateString(); };
  const formatDateTime = (dt) => dt ? new Date(dt).toLocaleString() : '';
  const formatTime = (time) => time?.substring(0, 5);

  const handleMergeSearch = (val) => {
    setMergeSearch(val);
    clearTimeout(mergeTimer.current);
    if (val.trim().length < 2) { setMergeResults([]); return; }
    mergeTimer.current = setTimeout(async () => {
      setMergeSearching(true);
      try {
        const res = await axios.get(`${API}/admin/customers?q=${encodeURIComponent(val.trim())}`);
        const list = res.data.data || res.data.customers || res.data;
        setMergeResults((Array.isArray(list) ? list : []).filter(c => c.id !== customerId));
      } catch { setMergeResults([]); }
      setMergeSearching(false);
    }, 300);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/admin/customers/${customerId}/profile`);
        setProfile(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [customerId]);

  const startEditing = () => {
    setEditForm({
      first_name: customer?.first_name || '',
      last_name: customer?.last_name || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
      phone2: customer?.phone2 || '',
      preferred_language: customer?.preferred_language || 'fr',
      notify_email: customer?.notify_email ?? true,
      notify_sms: customer?.notify_sms ?? false,
      notify_marketing: customer?.notify_marketing ?? false,
      email_verified: customer?.email_verified ?? false,
      phone_verified: customer?.phone_verified ?? false,
      phone2_verified: customer?.phone2_verified ?? false,
    });
    setSaveError(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setSaveError(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => {
      const next = { ...prev, [field]: value };
      // Auto-uncheck verified when contact field value changes from original
      if (field === 'email' && value !== customer.email) next.email_verified = false;
      if (field === 'phone' && value !== customer.phone) next.phone_verified = false;
      if (field === 'phone2' && value !== (customer.phone2 || '')) next.phone2_verified = false;
      return next;
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await axios.put(`${API}/admin/customers/${customerId}/profile`, editForm);
      // Update profile in place
      setProfile(prev => ({
        ...prev,
        customer: { ...prev.customer, ...res.data }
      }));
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Vehicle management handlers
  const openVehicleModal = (vehicle) => {
    if (vehicle) {
      setVehicleForm({ make: vehicle.make, model: vehicle.model, year: vehicle.year, license_plate: vehicle.license_plate || '', vin: vehicle.vin || '', color: vehicle.color || '' });
    } else {
      setVehicleForm({ make: '', model: '', year: new Date().getFullYear(), license_plate: '', vin: '', color: '' });
    }
    setVehicleError(null);
    setVehicleModal(vehicle || {});
  };

  const handleSaveVehicle = async () => {
    if (!vehicleForm.make || !vehicleForm.model || !vehicleForm.year) {
      setVehicleError(cp.vehicleRequired || 'Make, model, and year are required');
      return;
    }
    setVehicleSaving(true);
    setVehicleError(null);
    try {
      if (vehicleModal.id) {
        const res = await axios.put(`${API}/admin/vehicles/${vehicleModal.id}`, vehicleForm);
        setProfile(prev => ({ ...prev, vehicles: prev.vehicles.map(v => v.id === vehicleModal.id ? res.data : v) }));
      } else {
        const res = await axios.post(`${API}/admin/customers/${customerId}/vehicles`, vehicleForm);
        setProfile(prev => ({ ...prev, vehicles: [res.data, ...prev.vehicles] }));
      }
      setVehicleModal(null);
    } catch (err) {
      setVehicleError(err.response?.data?.error || 'Save failed');
    } finally {
      setVehicleSaving(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm(cp.deleteVehicleConfirm || 'Delete this vehicle? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${vehicleId}`);
      setProfile(prev => ({ ...prev, vehicles: prev.vehicles.filter(v => v.id !== vehicleId) }));
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  // Access customer before render guards since startEditing references it
  const customer = profile?.customer;

  if (loading) {
    return (
      <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content" style={{ background: '#0C1220', color: '#E8ECF0' }}>
            <div className="modal-body" style={{ textAlign: 'center', padding: '4rem' }}>
              <div style={{ color: '#D42B2B', fontSize: '1.2rem' }}>{t.common?.loading || 'Loading...'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
        <div className="modal-dialog" onClick={e => e.stopPropagation()}>
          <div className="modal-content" style={{ background: '#0C1220', color: '#E8ECF0' }}>
            <div className="modal-body" style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ color: '#D42B2B', marginBottom: '1rem' }}>{error}</div>
              <button className="btn btn-outline-primary" onClick={onClose}>{t.common?.close || 'Close'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { appointments, vehicles, reviews, messages, reminders, consents } = profile;

  const statusBadge = (status) => {
    const colors = {
      pending: '#eab308', confirmed: '#3b82f6', in_progress: '#f97316',
      completed: '#22c55e', cancelled: '#ef4444', scheduled: '#3b82f6',
      sent: '#22c55e', failed: '#ef4444'
    };
    return { display: 'inline-block', padding: '0.15rem 0.5rem', fontSize: '0.7rem', borderRadius: '3px',
      background: `${colors[status] || '#94A3B8'}22`, color: colors[status] || '#94A3B8' };
  };

  const verifiedBadge = (verified) => (
    <span style={{ color: verified ? '#22c55e' : '#ef4444', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
      {verified ? (cp.verified || 'Verified') : (cp.notVerified || 'Not verified')}
    </span>
  );

  const sections = [
    ['identity', cp.identity || 'Identity'],
    ['appointments', cp.appointmentsSection || 'Appointments'],
    ['vehicles', cp.vehiclesSection || 'Vehicles'],
    ['reviews', cp.reviewsSection || 'Reviews'],
    ['messages', cp.messagesSection || 'Messages'],
    ['reminders', cp.remindersSection || 'Reminders']
  ];

  const upcomingAppts = appointments.filter(a => {
    const d = new Date(String(a.appointment_date).substring(0, 10) + 'T12:00:00');
    return d >= new Date(new Date().toDateString()) && a.status !== 'cancelled';
  });
  const lastAppt = appointments.find(a => a.status === 'completed');

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div className="modal-dialog modal-xl" style={{ maxWidth: '900px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-content" style={{ background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', color: '#E8ECF0' }}>
          {/* Header */}
          <div className="modal-header" style={{ borderBottom: '1px solid rgba(148,163,184,0.15)', padding: '1.2rem 1.5rem' }}>
            <div>
              <h5 className="modal-title" style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.5rem', color: '#D42B2B', margin: 0 }}>
                {customer.first_name} {customer.last_name}
              </h5>
              <div style={{ fontSize: '0.8rem', color: '#94A3B8', marginTop: '0.25rem' }}>
                {cp.customerSince || 'Customer since'} {formatDate(customer.created_at)}
                {customer.import_source && <span style={{ marginLeft: '0.75rem' }}>({cp.imported || 'Imported'})</span>}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => setShowMergeSearch(!showMergeSearch)}
                style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
              >
                {cp.mergeWith || 'Merge with\u2026'}
              </button>
              <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={onClose}></button>
            </div>
          </div>

          {/* Merge search panel */}
          {showMergeSearch && (
            <div style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid rgba(148,163,184,0.15)', background: 'rgba(212,43,43,0.04)' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={mg.searchPlaceholder || 'Search customer to merge with\u2026'}
                  value={mergeSearch}
                  onChange={e => handleMergeSearch(e.target.value)}
                  autoFocus
                  style={{ maxWidth: '400px' }}
                />
                {mergeSearching && (
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem', color: '#94A3B8' }}>
                    {t.common?.loading || 'Loading\u2026'}
                  </span>
                )}
              </div>
              {mergeResults.length > 0 && (
                <div style={{ marginTop: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
                  {mergeResults.slice(0, 10).map(c => (
                    <div
                      key={c.id}
                      onClick={() => { setMergeTargetId(c.id); setShowMergeSearch(false); setMergeSearch(''); setMergeResults([]); }}
                      style={{
                        padding: '0.5rem 0.75rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', borderBottom: '1px solid rgba(148,163,184,0.08)',
                        background: '#111827', marginBottom: '2px'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,43,43,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = '#111827'}
                    >
                      <div>
                        <span style={{ color: '#E8ECF0', fontSize: '0.85rem' }}>{c.first_name} {c.last_name}</span>
                        <span style={{ color: '#64748B', fontSize: '0.75rem', marginLeft: '0.75rem' }}>{c.email}</span>
                      </div>
                      <span style={{ color: '#94A3B8', fontSize: '0.75rem' }}>{c.phone || ''}</span>
                    </div>
                  ))}
                </div>
              )}
              {mergeSearch.trim().length >= 2 && !mergeSearching && mergeResults.length === 0 && (
                <div style={{ fontSize: '0.8rem', color: '#94A3B8', marginTop: '0.5rem' }}>
                  {t.admin?.search?.noResults || 'No results found'}
                </div>
              )}
            </div>
          )}

          {/* Section tabs */}
          <div style={{ padding: '0 1.5rem', borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
            <div style={{ display: 'flex', gap: '0', overflowX: 'auto' }}>
              {sections.map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  style={{
                    background: 'transparent', border: 'none', borderBottom: activeSection === key ? '2px solid #D42B2B' : '2px solid transparent',
                    color: activeSection === key ? '#D42B2B' : '#94A3B8', padding: '0.75rem 1rem', fontSize: '0.8rem',
                    cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.05em'
                  }}
                >
                  {label}
                  {key === 'appointments' && ` (${appointments.length})`}
                  {key === 'reminders' && reminders.length > 0 && ` (${reminders.length})`}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="modal-body" style={{ padding: '1.5rem', maxHeight: '65vh', overflowY: 'auto' }}>

            {/* IDENTITY */}
            {activeSection === 'identity' && (
              <div>
                {/* Quick stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div style={{ background: '#111827', padding: '1rem', textAlign: 'center', border: '1px solid rgba(148,163,184,0.1)' }}>
                    <div style={{ fontSize: '1.8rem', color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif" }}>{appointments.length}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{cp.totalAppts || 'Total Appts'}</div>
                  </div>
                  <div style={{ background: '#111827', padding: '1rem', textAlign: 'center', border: '1px solid rgba(148,163,184,0.1)' }}>
                    <div style={{ fontSize: '1.8rem', color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif" }}>{upcomingAppts.length}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{cp.upcoming || 'Upcoming'}</div>
                  </div>
                  <div style={{ background: '#111827', padding: '1rem', textAlign: 'center', border: '1px solid rgba(148,163,184,0.1)' }}>
                    <div style={{ fontSize: '1.8rem', color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif" }}>{reviews.length}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{cp.reviewCount || 'Reviews'}</div>
                  </div>
                  <div style={{ background: '#111827', padding: '1rem', textAlign: 'center', border: '1px solid rgba(148,163,184,0.1)' }}>
                    <div style={{ fontSize: '1.8rem', color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif" }}>{vehicles.length}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{cp.vehicleCount || 'Vehicles'}</div>
                  </div>
                </div>

                {/* Contact info */}
                <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1.5rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '0.85rem', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>{cp.contactInfo || 'Contact Info'}</h4>
                    {canEdit && !editing && (
                      <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: '0.75rem' }} onClick={startEditing}>
                        {cp.editButton || 'Edit'}
                      </button>
                    )}
                  </div>

                  {!editing ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem' }}>{t.admin.customerHeaders.email}</div>
                        <div style={{ fontSize: '0.9rem' }}>{customer.email} {verifiedBadge(customer.email_verified)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem' }}>{t.admin.customerHeaders.phone}</div>
                        <div style={{ fontSize: '0.9rem' }}>{customer.phone || t.admin.na} {customer.phone && verifiedBadge(customer.phone_verified)}</div>
                      </div>
                      {customer.phone2 && (
                        <div>
                          <div style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem' }}>{cp.phone2 || 'Secondary Phone'}</div>
                          <div style={{ fontSize: '0.9rem' }}>{customer.phone2} {verifiedBadge(customer.phone2_verified)}</div>
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem' }}>{cp.language || 'Language'}</div>
                        <div style={{ fontSize: '0.9rem' }}>{customer.preferred_language === 'en' ? 'English' : 'Français'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem' }}>{cp.lastLogin || 'Last Login'}</div>
                        <div style={{ fontSize: '0.9rem' }}>{customer.last_login_at ? formatDateTime(customer.last_login_at) : t.admin.never}</div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem', display: 'block' }}>{cp.firstName || 'First Name'}</label>
                        <input className="form-control form-control-sm" value={editForm.first_name} onChange={e => handleEditChange('first_name', e.target.value)} />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem', display: 'block' }}>{cp.lastName || 'Last Name'}</label>
                        <input className="form-control form-control-sm" value={editForm.last_name} onChange={e => handleEditChange('last_name', e.target.value)} />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem', display: 'block' }}>{t.admin.customerHeaders.email}</label>
                        <input className="form-control form-control-sm" type="email" value={editForm.email} onChange={e => handleEditChange('email', e.target.value)} />
                        <label style={{ fontSize: '0.65rem', color: '#64748B', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={editForm.email_verified} onChange={e => setEditForm(prev => ({ ...prev, email_verified: e.target.checked }))} style={{ accentColor: '#22c55e' }} />
                          {cp.verified || 'Verified'}
                        </label>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem', display: 'block' }}>{t.admin.customerHeaders.phone}</label>
                        <input className="form-control form-control-sm" value={editForm.phone} onChange={e => handleEditChange('phone', e.target.value)} />
                        <label style={{ fontSize: '0.65rem', color: '#64748B', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={editForm.phone_verified} onChange={e => setEditForm(prev => ({ ...prev, phone_verified: e.target.checked }))} style={{ accentColor: '#22c55e' }} />
                          {cp.verified || 'Verified'}
                        </label>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem', display: 'block' }}>{cp.phone2 || 'Secondary Phone'}</label>
                        <input className="form-control form-control-sm" value={editForm.phone2} onChange={e => handleEditChange('phone2', e.target.value)} />
                        <label style={{ fontSize: '0.65rem', color: '#64748B', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={editForm.phone2_verified} onChange={e => setEditForm(prev => ({ ...prev, phone2_verified: e.target.checked }))} style={{ accentColor: '#22c55e' }} />
                          {cp.verified || 'Verified'}
                        </label>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem', display: 'block' }}>{cp.language || 'Language'}</label>
                        <select className="form-select form-select-sm" value={editForm.preferred_language} onChange={e => setEditForm(prev => ({ ...prev, preferred_language: e.target.value }))}>
                          <option value="fr">Français</option>
                          <option value="en">English</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notification preferences */}
                <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1.5rem', marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '0.85rem', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem' }}>{cp.notifications || 'Notification Preferences'}</h4>
                  {!editing ? (
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: customer.notify_email ? '#22c55e' : '#64748B' }}>{customer.notify_email ? '\u2713' : '\u2717'}</span>
                        <span style={{ fontSize: '0.85rem' }}>{cp.emailNotif || 'Email'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: customer.notify_sms ? '#22c55e' : '#64748B' }}>{customer.notify_sms ? '\u2713' : '\u2717'}</span>
                        <span style={{ fontSize: '0.85rem' }}>SMS</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: customer.notify_marketing ? '#22c55e' : '#64748B' }}>{customer.notify_marketing ? '\u2713' : '\u2717'}</span>
                        <span style={{ fontSize: '0.85rem' }}>{cp.marketing || 'Marketing'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: customer.push_subscribed ? '#22c55e' : '#64748B' }}>{customer.push_subscribed ? '\u2713' : '\u2717'}</span>
                        <span style={{ fontSize: '0.85rem' }}>Push</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={editForm.notify_email} onChange={e => setEditForm(prev => ({ ...prev, notify_email: e.target.checked }))} style={{ accentColor: '#22c55e' }} />
                        {cp.emailNotif || 'Email'}
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={editForm.notify_sms} onChange={e => setEditForm(prev => ({ ...prev, notify_sms: e.target.checked }))} style={{ accentColor: '#22c55e' }} />
                        SMS
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={editForm.notify_marketing} onChange={e => setEditForm(prev => ({ ...prev, notify_marketing: e.target.checked }))} style={{ accentColor: '#22c55e' }} />
                        {cp.marketing || 'Marketing'}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: customer.push_subscribed ? '#22c55e' : '#64748B' }}>{customer.push_subscribed ? '\u2713' : '\u2717'}</span>
                        <span style={{ fontSize: '0.85rem', color: '#64748B' }}>Push ({cp.readOnly || 'read-only'})</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Save/Cancel buttons when editing */}
                {editing && (
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
                    <button className="btn btn-danger btn-sm" onClick={saveEdit} disabled={saving}>
                      {saving ? (t.common?.loading || 'Saving...') : (cp.saveChanges || 'Save Changes')}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={cancelEditing} disabled={saving}>
                      {t.common?.cancel || 'Cancel'}
                    </button>
                    {saveError && <span style={{ color: '#ef4444', fontSize: '0.8rem' }}>{saveError}</span>}
                  </div>
                )}

                {/* Last appointment */}
                {lastAppt && (
                  <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1.5rem' }}>
                    <h4 style={{ fontSize: '0.85rem', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem' }}>{cp.lastAppt || 'Last Completed Appointment'}</h4>
                    <div style={{ fontSize: '0.9rem' }}>
                      <span style={{ color: '#E8ECF0' }}>{Array.isArray(lastAppt.services) && lastAppt.services.length > 0
                        ? lastAppt.services.map(s => s.name_fr || s.name).join(' + ')
                        : (lastAppt.service_name_fr || lastAppt.service_name)}</span>
                      <span style={{ color: '#94A3B8', marginLeft: '1rem' }}>{formatDate(lastAppt.appointment_date)} {t.dashboard?.at || 'at'} {formatTime(lastAppt.appointment_time)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* APPOINTMENTS */}
            {activeSection === 'appointments' && (
              <div>
                {appointments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>{cp.noAppointments || 'No appointments'}</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-dark table-hover" style={{ fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          <th>{t.admin.tableHeaders.date}</th>
                          <th>{t.admin.tableHeaders.time}</th>
                          <th>{t.admin.tableHeaders.service}</th>
                          <th>{t.admin.tableHeaders.vehicle}</th>
                          <th>{t.admin.tableHeaders.status}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appointments.map(a => (
                          <tr key={a.id}>
                            <td>{formatDate(a.appointment_date)}</td>
                            <td>{formatTime(a.appointment_time)}</td>
                            <td>{Array.isArray(a.services) && a.services.length > 0
                              ? a.services.map(s => s.name_fr || s.name).join(' + ')
                              : (a.service_name_fr || a.service_name)}</td>
                            <td>{a.year && a.make ? `${a.year} ${a.make} ${a.model}` : t.admin.na}</td>
                            <td><span style={statusBadge(a.status)}>{t.dashboard?.status?.[a.status] || a.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* VEHICLES */}
            {activeSection === 'vehicles' && (
              <div>
                {canEdit && (
                  <button className="btn btn-outline-danger btn-sm mb-3" onClick={() => openVehicleModal(null)}>
                    + {cp.addVehicle || 'Add Vehicle'}
                  </button>
                )}
                {vehicles.length === 0 && !vehicleModal ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>{cp.noVehicles || 'No vehicles'}</div>
                ) : (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {vehicles.map(v => (
                      <div key={v.id} style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1.25rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontSize: '1.1rem', color: '#E8ECF0', marginBottom: '0.5rem' }}>{v.year} {v.make} {v.model}</div>
                            <div style={{ fontSize: '0.8rem', color: '#94A3B8' }}>
                              {v.license_plate && <span>{cp.plate || 'Plate'}: {v.license_plate}</span>}
                              {v.color && <span style={{ marginLeft: '1rem' }}>{cp.color || 'Color'}: {v.color}</span>}
                              {v.vin && <span style={{ marginLeft: '1rem' }}>VIN: {v.vin}</span>}
                            </div>
                          </div>
                          {canEdit && (
                            <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                              <button className="btn btn-outline-secondary btn-sm" onClick={() => openVehicleModal(v)}>
                                {cp.editButton || 'Edit'}
                              </button>
                              <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteVehicle(v.id)}>
                                {cp.deleteButton || 'Delete'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Vehicle Add/Edit Modal */}
                {vehicleModal && (
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setVehicleModal(null)}>
                    <div style={{ background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', padding: '2rem', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}
                      onClick={e => e.stopPropagation()}>
                      <h4 style={{ fontFamily: "'Rajdhani', sans-serif", color: '#D42B2B', marginBottom: '1.5rem' }}>
                        {vehicleModal.id ? (cp.editVehicle || 'Edit Vehicle') : (cp.addVehicle || 'Add Vehicle')}
                      </h4>
                      <VehicleSelector
                        value={vehicleForm}
                        onChange={setVehicleForm}
                        t={t}
                        showVin={true}
                        showColor={true}
                      />
                      {vehicleError && (
                        <div style={{ color: '#D42B2B', fontSize: '0.85rem', marginBottom: '1rem' }}>{vehicleError}</div>
                      )}
                      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => setVehicleModal(null)} disabled={vehicleSaving}>
                          {t.common?.cancel || 'Cancel'}
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveVehicle} disabled={vehicleSaving}>
                          {vehicleSaving ? (t.common?.saving || 'Saving...') : (t.common?.save || 'Save')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* REVIEWS */}
            {activeSection === 'reviews' && (
              <div>
                {reviews.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>{t.admin.noReviews}</div>
                ) : (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {reviews.map(r => (
                      <div key={r.id} style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1.25rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <span style={{ color: '#94A3B8', fontSize: '0.8rem' }}>{Array.isArray(r.services) && r.services.length > 0
                            ? r.services.map(s => s.name_fr || s.name).join(' + ')
                            : (r.service_name_fr || r.service_name)} - {formatDate(r.created_at)}</span>
                          <span style={{ color: '#D42B2B' }}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                        </div>
                        {r.comment && <div style={{ color: '#E8ECF0', fontSize: '0.9rem', fontStyle: 'italic' }}>"{r.comment}"</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* MESSAGES */}
            {activeSection === 'messages' && (
              <div>
                {messages.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>{t.admin.noMessages}</div>
                ) : (
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {messages.map(m => (
                      <div key={m.id} style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '0.9rem', color: '#E8ECF0' }}>{m.subject}</div>
                          <div style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{formatDateTime(m.created_at)} - {m.reply_count} {cp.replies || 'replies'}</div>
                        </div>
                        <span style={statusBadge(m.status)}>{m.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* REMINDERS */}
            {activeSection === 'reminders' && (
              <div>
                {reminders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>{cp.noReminders || 'No reminders'}</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-dark table-hover" style={{ fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          <th>{cp.reminderDate || 'Reminder Date'}</th>
                          <th>{t.admin.tableHeaders.service}</th>
                          <th>{cp.channels || 'Channels'}</th>
                          <th>{t.admin.tableHeaders.status}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reminders.map(r => (
                          <tr key={r.id}>
                            <td>{formatDateTime(r.remind_at)}</td>
                            <td>{r.service_name_fr || r.service_name}</td>
                            <td>
                              {r.channel_email && <span style={{ marginRight: '0.5rem' }}>Email</span>}
                              {r.channel_sms && <span>SMS</span>}
                            </td>
                            <td>
                              <span style={statusBadge(r.status)}>{r.status}</span>
                              {r.last_error && <div style={{ fontSize: '0.7rem', color: '#ef4444', marginTop: '0.25rem' }}>{r.last_error}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Merge Modal */}
      {mergeTargetId && (
        <MergeCustomersModal
          primaryId={customerId}
          secondaryId={mergeTargetId}
          onClose={() => setMergeTargetId(null)}
          onMerged={() => { setMergeTargetId(null); if (onMerged) onMerged(); onClose(); }}
          t={t}
        />
      )}
    </div>
  );
}
